import React, { useState, useEffect, useCallback } from 'react';
import {
  requestPrinter, disconnect, getPrinterStatus,
  setPaperWidth, loadPrinterConfig, printTestTicket,
  PrinterStatus,
} from '../../services/thermalPrinterService';

interface Props {
  onClose?: () => void;
}

const PrinterSetup: React.FC<Props> = ({ onClose }) => {
  const [status,    setStatus]    = useState<PrinterStatus>(getPrinterStatus());
  const [paperWidth, setPaper]    = useState<58 | 80>(getPrinterStatus().paperWidth);
  const [loading,   setLoading]   = useState(false);
  const [testing,   setTesting]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [success,   setSuccess]   = useState<string | null>(null);

  const refresh = useCallback(() => setStatus(getPrinterStatus()), []);

  useEffect(() => {
    loadPrinterConfig();
    refresh();
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [refresh]);

  const handleConnect = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await requestPrinter(paperWidth);
      refresh();
      setSuccess('Impresora conectada correctamente');
    } catch (e: any) {
      if (e.message?.includes('cancelled') || e.name === 'NotFoundError') {
        setError('Búsqueda cancelada');
      } else {
        setError(e.message || 'No se pudo conectar a la impresora');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = () => {
    disconnect();
    refresh();
    setSuccess(null);
    setError(null);
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    try {
      await printTestTicket();
      setSuccess('Ticket de prueba enviado ✓');
    } catch (e: any) {
      setError(e.message || 'Error al imprimir');
    } finally {
      setTesting(false);
    }
  };

  const handlePaperChange = (w: 58 | 80) => {
    setPaper(w);
    setPaperWidth(w);
  };

  const isConnected = status.connected;
  const supported   = !!navigator.bluetooth;

  return (
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 pt-6 pb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg,#0f172a,#1e40af)' }}>
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-base font-black text-slate-800">Impresora Térmica</h3>
          <p className="text-xs text-slate-400">Conexión Bluetooth ESC/POS</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        )}
      </div>

      <div className="px-6 pb-6 space-y-4">

        {/* Estado */}
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl"
          style={{ background: isConnected ? 'rgba(16,185,129,0.08)' : 'rgba(100,116,139,0.08)',
                   border: `1px solid ${isConnected ? 'rgba(16,185,129,0.25)' : 'rgba(100,116,139,0.2)'}` }}>
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isConnected ? 'bg-emerald-500' : 'bg-slate-400'}`}
            style={isConnected ? { boxShadow: '0 0 6px #10b981' } : {}} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: isConnected ? '#065f46' : '#475569' }}>
              {isConnected ? 'Conectada' : 'Sin conexión'}
            </p>
            {status.deviceName && (
              <p className="text-xs text-slate-400 truncate">{status.deviceName}</p>
            )}
          </div>
          {isConnected && (
            <span className="text-xs font-bold px-2 py-1 rounded-lg"
              style={{ background: 'rgba(16,185,129,0.15)', color: '#059669' }}>
              {status.paperWidth}mm
            </span>
          )}
        </div>

        {/* Aviso si Bluetooth no soportado */}
        {!supported && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-xl"
            style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
            <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
            <p className="text-xs text-amber-700">
              Web Bluetooth no disponible. Usa <strong>Chrome</strong> en Android o Chrome desktop con flag habilitado.
            </p>
          </div>
        )}

        {/* Ancho de papel */}
        {!isConnected && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Ancho de papel</p>
            <div className="flex gap-2">
              {([58, 80] as const).map(w => (
                <button key={w} type="button" onClick={() => handlePaperChange(w)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all"
                  style={{
                    background: paperWidth === w ? 'linear-gradient(135deg,#1e40af,#2563eb)' : '#f1f5f9',
                    color: paperWidth === w ? '#fff' : '#64748b',
                    boxShadow: paperWidth === w ? '0 4px 12px rgba(37,99,235,0.3)' : 'none',
                  }}>
                  {w}mm
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Feedback */}
        {error && (
          <p className="text-xs font-semibold text-red-600 px-3 py-2 rounded-lg"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)' }}>
            {error}
          </p>
        )}
        {success && (
          <p className="text-xs font-semibold text-emerald-700 px-3 py-2 rounded-lg"
            style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
            {success}
          </p>
        )}

        {/* Botones */}
        <div className="flex flex-col gap-2">
          {!isConnected ? (
            <button type="button" onClick={handleConnect}
              disabled={loading || !supported}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg,#1e40af,#2563eb)', boxShadow: '0 4px 14px rgba(37,99,235,0.4)' }}>
              {loading ? (
                <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>Buscando…</>
              ) : (
                <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"/>
                </svg>Conectar Impresora</>
              )}
            </button>
          ) : (
            <>
              <button type="button" onClick={handleTest} disabled={testing}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
                style={{ background: 'rgba(37,99,235,0.08)', color: '#1d4ed8', border: '1px solid rgba(37,99,235,0.2)' }}>
                {testing ? (
                  <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>Imprimiendo…</>
                ) : (
                  <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
                  </svg>Imprimir ticket de prueba</>
                )}
              </button>
              <button type="button" onClick={handleDisconnect}
                className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all hover:bg-red-50"
                style={{ color: '#ef4444', border: '1px solid #fecaca' }}>
                Desconectar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrinterSetup;
